'use client';

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ProviderProps } from '../types/types';
import { requestHandler } from '../utils/requestHandler';
import { Department } from '@prisma/client/browser';
import { getDepartments } from '../utils/handlers/client/department';
import { useToast } from './toast-provider';
export const dynamic = 'force-dynamic';
const DepartmentContext = createContext({
  error: '',
  map: {},
  list: [],
  setList: _list => {},
  setMap: _map => {},
  metaData: {},
  getAllDepartments: async (_companySlug, _setIsLoading) => {}
});
const useDepartmentContext = () => useContext(DepartmentContext);
const DepartmentProvider = ({
  children
}) => {
  const [error, setError] = useState('');
  const [list, setList] = useState([]);
  const [map, setMap] = useState({});
  const [metaData, setMetaData] = useState({});
  const {
    showErrorToast
  } = useToast();
  const fetchedSlugsRef = useRef(new Set());
  const getAllDepartments = useCallback(async (companySlug, setIsLoading) => {
    if (fetchedSlugsRef.current.has(companySlug)) return;
    fetchedSlugsRef.current.add(companySlug);
    await requestHandler(async () => await getDepartments(companySlug), res => {
      const {
        data
      } = res;
      const {
        Departments = [],
        ...nextMetaData
      } = data ?? {};
      const departmentMap = Departments.reduce((acc, department) => {
        acc[department.id] = department;
        return acc;
      }, {});
      setMap(departmentMap);
      setList(Object.keys(departmentMap).map(Number));
      setMetaData(nextMetaData);
    }, errorMsg => {
      fetchedSlugsRef.current.delete(companySlug);
      if (errorMsg === 'Company not found.') {
        window.location.replace('/auth/logout');
      }
      showErrorToast(errorMsg);
    }, setIsLoading);
  }, [showErrorToast]);
  const contextValue = useMemo(() => ({
    error,
    map,
    list,
    metaData,
    setMap,
    setList,
    getAllDepartments
  }), [error, map, list, metaData, getAllDepartments]);
  return <DepartmentContext.Provider value={contextValue}>{children}</DepartmentContext.Provider>;
};
export { DepartmentContext, DepartmentProvider, useDepartmentContext };